import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { StandardLayout } from "@/components/StandardLayout";

// Home is loaded eagerly — it's the critical first-paint page
import HomePage from "@/pages/Home";

// All other pages are lazy-loaded — their JS only downloads when the route is visited
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AffiliateDisclosure = lazy(() => import("@/pages/AffiliateDisclosure"));
const AdminDashboard = lazy(() => import("@/pages/Admin"));
const Blog = lazy(() => import("@/pages/Blog"));
const AutomationBlogPost = lazy(() => import("@/pages/AutomationLandingPage"));
const AutomationROIBlogPost = lazy(() => import("@/pages/AutomationROIBlogPost"));
const AutomationWorkflowBlogPost = lazy(() => import("@/pages/AutomationWorkflowBlogPost"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SopPortal = lazy(() => import("@/pages/SopPortal"));
const BusinessAudit = lazy(() => import("@/pages/BusinessAudit"));

// Minimal fallback shown while lazy chunks load
const PageLoader = () => (
  <div style={{ minHeight: "100vh", background: "#09090b" }} />
);

/*
  Wireframe watermark — moved to CSS (App.css .yukti-watermark::before) to
  eliminate it as an LCP candidate. DOM text nodes are scored by browsers for LCP;
  CSS ::before pseudo-elements are not.
*/

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <TooltipProvider>
          {/* CSS-only watermark — see .yukti-watermark in App.css */}
          <div className="yukti-watermark" aria-hidden="true" />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Home manages its own full-viewport layout */}
              <Route path="/" element={<HomePage />} />

              {/* Blog — lazy loaded */}
              <Route path="/blog" element={<StandardLayout><Blog /></StandardLayout>} />
              <Route path="/blog/ai-automation-for-small-business" element={<StandardLayout><AutomationBlogPost /></StandardLayout>} />
              <Route path="/blog/how-to-calculate-roi-on-business-automation" element={<StandardLayout><AutomationROIBlogPost /></StandardLayout>} />
              <Route path="/blog/ai-workflow-automation-small-business" element={<StandardLayout><AutomationWorkflowBlogPost /></StandardLayout>} />

              {/* Auxiliary pages — lazy loaded */}
              <Route path="/about" element={<StandardLayout><About /></StandardLayout>} />
              <Route path="/contact" element={<StandardLayout><Contact /></StandardLayout>} />
              <Route path="/privacy" element={<StandardLayout><Privacy /></StandardLayout>} />
              <Route path="/terms" element={<StandardLayout><Terms /></StandardLayout>} />
              <Route path="/affiliate-disclosure" element={<StandardLayout><AffiliateDisclosure /></StandardLayout>} />
              <Route path="/admin" element={<StandardLayout><AdminDashboard /></StandardLayout>} />
              <Route path="/sop" element={<SopPortal />} />
              <Route path="/business-audit" element={<BusinessAudit />} />


              {/* Custom 404 — catch-all */}
              <Route path="*" element={<StandardLayout><NotFound /></StandardLayout>} />
            </Routes>
          </Suspense>

          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              className: "bg-zinc-900 border border-white/10 text-white shadow-none",
            }}
          />
        </TooltipProvider>
      </BrowserRouter>
      <Analytics />
    </HelmetProvider>
  );
}

export default App;
